<script setup lang="ts">
import type { ModelKey } from '~/features/my-wallet/constants/my-wallet'
import type { PerformanceBarLabelState, PerformanceThresholds, ProgressBarSegment } from '~/features/my-wallet/types/charts'
import { useI18n } from 'vue-i18n'
import IconLock from '~icons/material-symbols/lock-outline'
import IconStar from '~icons/material-symbols/star'
import ProgressBar from '~/features/my-wallet/components/ProgressBar.vue'
import { PERFORMANCE_WINDOW_DAYS } from '~/features/my-wallet/constants/my-wallet'
import { buildPerformanceRanges } from '~/features/my-wallet/utils/charts'

interface Props {
  percentage: number | null
  thresholds: PerformanceThresholds
  variant?: 'full' | 'compact'
  muted?: boolean
  commercialException?: boolean
  labelStates?: Partial<Record<ModelKey, PerformanceBarLabelState>>
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'compact',
  muted: false,
  commercialException: false,
  labelStates: () => ({}),
})

const { t } = useI18n()

const clampedPercentage = computed(() => Math.max(0, Math.min(100, props.percentage ?? 0)))
const hasPercentage = computed(() => props.percentage !== null)

const LABEL_STATE_WITHOUT_API: Record<ModelKey, PerformanceBarLabelState> = {
  C: 'unlocked',
  B: 'locked',
  A: 'locked',
}

function resolveLabelState(model: ModelKey): PerformanceBarLabelState {
  if (props.muted)
    return 'unlocked'
  return props.labelStates[model] ?? LABEL_STATE_WITHOUT_API[model]
}

const isModelALocked = computed(() => resolveLabelState('A') === 'locked')

const segments = computed<ProgressBarSegment[]>(() => {
  const barSegments: ProgressBarSegment[] = []
  const isFullVariant = props.variant === 'full'

  if (isFullVariant && !props.muted && !props.commercialException && isModelALocked.value)
    barSegments.push({ from: props.thresholds.modelA, to: 100, tone: 'locked' })

  if (!hasPercentage.value)
    return barSegments

  if (props.muted) {
    barSegments.push({ from: 0, to: clampedPercentage.value, tone: 'muted' })
    return barSegments
  }

  barSegments.push(...buildPerformanceRanges({
    percentage: clampedPercentage.value,
    thresholds: props.thresholds,
    showModelA: !isFullVariant || !isModelALocked.value || props.commercialException,
    commercialException: props.commercialException,
  }))

  return barSegments
})

const thresholdMarkers = computed(() => [props.thresholds.modelB, props.thresholds.modelA])

const modelLabels = computed(() => {
  const { modelB, modelA } = props.thresholds
  return [
    { model: 'C' as const, position: modelB / 2 },
    { model: 'B' as const, position: (modelB + modelA) / 2 },
    { model: 'A' as const, position: (modelA + 100) / 2 },
  ].map(modelLabel => ({
    ...modelLabel,
    state: resolveLabelState(modelLabel.model),
  }))
})

const scaleTicks = computed(() => [0, props.thresholds.modelB, props.thresholds.modelA, 100])

const labelStatesAria = computed(() => props.variant !== 'full'
  ? []
  : modelLabels.value
      .filter(modelLabel => modelLabel.state !== 'unlocked')
      .map(modelLabel => t('my_wallet.receiving_model.performance.bar.state_aria', {
        model: modelLabel.model,
        state: t(`my_wallet.receiving_model.performance.bar.${modelLabel.state}`),
      })))

const ariaLabel = computed(() => {
  const percentageDescription = hasPercentage.value
    ? t('my_wallet.receiving_model.performance.bar.aria', { percentage: props.percentage, days: PERFORMANCE_WINDOW_DAYS })
    : t('my_wallet.receiving_model.performance.bar.aria_not_evaluated')
  if (labelStatesAria.value.length === 0)
    return percentageDescription
  return t('my_wallet.receiving_model.performance.bar.aria_with_states', {
    summary: percentageDescription,
    states: labelStatesAria.value.join(t('my_wallet.list_separator')),
  })
})
</script>

<template>
  <div
    class="performance-bar"
    :class="[`-${variant}`, { '-muted': muted }]"
    role="img"
    :aria-label="ariaLabel"
  >
    <div v-if="variant === 'full'" class="performance-bar-labels">
      <span
        v-for="modelLabel in modelLabels"
        :key="modelLabel.model"
        class="performance-bar-label"
        :class="`-${modelLabel.state}`"
        :style="{ left: `${modelLabel.position}%` }"
      >
        <IconLock v-if="modelLabel.state === 'locked'" class="performance-bar-label-icon" aria-hidden="true" />
        <IconStar v-else-if="modelLabel.state === 'waived'" class="performance-bar-label-icon" aria-hidden="true" />
        <span class="performance-bar-label-full">{{ t('my_wallet.receiving_model.performance.bar.label', { model: modelLabel.model }) }}</span>
        <span class="performance-bar-label-short">{{ modelLabel.model }}</span>
      </span>
    </div>

    <ProgressBar
      :segments="segments"
      :markers="thresholdMarkers"
      :size="variant === 'full' ? 'large' : 'small'"
    />

    <div v-if="variant === 'full'" class="performance-bar-ticks" aria-hidden="true">
      <span
        v-for="(tick, index) in scaleTicks"
        :key="tick"
        class="performance-bar-tick"
        :class="{ '-first': index === 0, '-last': index === scaleTicks.length - 1 }"
        :style="{ left: `${tick}%` }"
      >
        {{ t('my_wallet.receiving_model.performance.bar.mark', { value: tick }) }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.performance-bar {
  @apply flex flex-col w-full;

  &.-full {
    @apply gap-2;
  }

  &.-muted {
    filter: grayscale(1);
  }

  .performance-bar-labels,
  .performance-bar-ticks {
    @apply relative w-full h-4;
  }

  .performance-bar-label {
    @apply absolute inline-flex items-center gap-1 whitespace-nowrap;
    @apply fonts-body-small-bold text-brand-secondary-dark;
    line-height: 1.5;
    transform: translateX(-50%);

    &.-locked {
      @apply text-brand-primary-light;
    }

    &.-waived {
      @apply text-brand-primary-medium;
    }
  }

  .performance-bar-label-short {
    @apply hidden;

    @screen xsm:system {
      @apply inline;
    }
  }

  .performance-bar-label-full {
    @screen xsm:system {
      @apply hidden;
    }
  }

  .performance-bar-label-icon {
    @apply w-3 h-3 flex-shrink-0;
  }

  .performance-bar-tick {
    @apply absolute fonts-body-small-regular text-neutral-low-light;
    line-height: 1.5;
    transform: translateX(-50%);

    &.-first {
      transform: none;
    }

    &.-last {
      transform: translateX(-100%);

      @screen xsm:system {
        @apply hidden;
      }
    }
  }
}
</style>
